import { Card, CardContent, CardHeader, CardTitle } from '@/client/components/ui/card';
import { Badge } from '@/client/components/ui/badge';
import { Package, Zap, HardDrive, Percent, FileText } from 'lucide-react';
import type { ClientPackage, ClientTranscription } from '@shared/types';

interface PackageCardProps {
  packageInfo: ClientPackage | null;
  transcription: ClientTranscription | null;
}

/**
 * Format bytes to human readable (for maxBandwidth - stored in bytes)
 * e.g., 3000000000000 = 3 TB
 */
function formatBytes(bytes: string | number): string {
  const num = typeof bytes === 'string' ? parseInt(bytes, 10) : bytes;
  if (num === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.floor(Math.log(num) / Math.log(k));
  return parseFloat((num / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

/**
 * Format megabytes to human readable (for maxStorage - stored in MB)
 * e.g., maxStorage=2048000 means ~2 TB (2,048,000 MB)
 */
function formatMegabytes(mb: string | number): string {
  const num = typeof mb === 'string' ? parseInt(mb, 10) : mb;
  if (num === 0) return '0 MB';
  if (num < 1024) return `${num} MB`;
  const gb = num / 1024;
  if (gb < 1024) return `${gb.toFixed(gb < 10 ? 1 : 0)} GB`;
  const tb = gb / 1024;
  return `${tb.toFixed(1)} TB`;
}

// Format currency
function formatCurrency(amount: string | number): string {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(num);
}

export function PackageCard({ packageInfo, transcription }: PackageCardProps) {
  if (!packageInfo && !transcription) {
    return null;
  }

  const hasDiscounts =
    packageInfo &&
    (packageInfo.discountPercent > 0 ||
      packageInfo.discountBWOPercent > 0 ||
      packageInfo.discountSTOPercent > 0);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Package className="h-4 w-4" />
            Package / Billing
          </CardTitle>
          {packageInfo && (
            <Badge variant="default">{packageInfo.levelName}</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {packageInfo && (
          <>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-muted-foreground" />
                <div>
                  <div className="text-muted-foreground text-xs">Max BW</div>
                  <div>{formatBytes(packageInfo.maxBandwidth)}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <HardDrive className="h-4 w-4 text-muted-foreground" />
                <div>
                  <div className="text-muted-foreground text-xs">Storage</div>
                  <div>{formatMegabytes(packageInfo.maxStorage)}</div>
                </div>
              </div>
            </div>

            {hasDiscounts && (
              <div className="flex items-center gap-2 text-sm">
                <Percent className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Discounts:</span>
                <div className="flex gap-1">
                  {packageInfo.discountPercent > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {packageInfo.discountPercent}% General
                    </Badge>
                  )}
                  {packageInfo.discountBWOPercent > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {packageInfo.discountBWOPercent}% BW
                    </Badge>
                  )}
                  {packageInfo.discountSTOPercent > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {packageInfo.discountSTOPercent}% STO
                    </Badge>
                  )}
                </div>
              </div>
            )}

            <div className="pt-2 border-t text-sm">
              <span className="text-muted-foreground">Monthly Rate:</span>{' '}
              <span className="font-medium">{formatCurrency(packageInfo.monthlyRate)}</span>
            </div>
          </>
        )}

        {transcription && (
          <div className="pt-2 border-t">
            <div className="flex items-center gap-2 text-sm">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Transcription:</span>
              <div className="flex gap-1 items-center">
                {transcription.autoOnPublish && (
                  <Badge variant="default" className="text-xs">Auto</Badge>
                )}
                <span>{transcription.monthlyBulkHrs}h bulk</span>
                {transcription.rateDiscount > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {transcription.rateDiscount}% off
                  </Badge>
                )}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
